<template>
    <div class="preorders-list">
        <header>{{ trans.orders.preorders }}</header>
        <div class="preorders-grid-container">
            <!-- Grid Header -->
            <div class="grid-header" :class="{ 'with-date': showDate }">
                <div class="grid-cell time-header">
                    <i class="far fa-clock"></i>
                </div>
                <div class="grid-cell name-header">{{ trans.orders.name }}</div>
                <div class="grid-cell guests-header">
                    <MultiSelect
                        class="select-preorder-state-component"
                        display="comma"
                        :placeholder="trans.orders.preordersWithoutReservations"
                        :maxSelectedLabels="1"
                        :selectedItemsLabel="tableReservationStateSelectionMessage"
                        v-model="stateSelection"
                        :options="props.filterOptions"
                        @update:modelValue="onUpdateTableReservationStateFilterSelection"
                        optionLabel="name"
                        filter
                    />

                    <i class="far fa-user"></i>
                </div>
            </div>

            <!-- ScrollableView for Grid Rows -->
            <ScrollableView class="grid-rows-scrollable" v-if="!props.isLoading">
                <div class="grid-rows">
                    <PreordersListRow
                        v-for="preorder in filteredPreorders"
                        :key="preorder.orderId"
                        :preorder="preorder"
                        :showDate="showDate"
                        @reservationDetailsChanged="onReservationDetailsChanged"
                        @preorderClick="onPreorderClick(preorder.orderId)"
                    />
                </div>
                <div v-if="filteredPreorders.length === 0">
                    <div class="no-preorders">{{ trans.orders.noOrdersFound }}</div>
                </div>
            </ScrollableView>

            <div v-if="isLoading" class="loading-placeholder">
                <div class="loading">
                    {{ translations.general.loading }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { IPreorderShift } from "@/data/model/Operator";
    import type { IPreorder, IReservationDetails } from "@/data/model/Order";
    import type { ILinkableEntity } from "@/data/model/Shared";
    // DateTime handled in child component
    import { trans } from "@/operator/localization/Trans";
    import { OrderDetailsModal } from "@/operator/modal/OrderDetailsModal";
    import { translations } from "@/shared/localization/translations";
    import { computed, ref } from "vue";

    import PreordersListRow from "./PreordersListRow.vue";
    import ScrollableView from "@/components/layout/ScrollableView.vue";
    import MultiSelect from "primevue/multiselect";

    type PreorderListItem = {
        listItemId: string;
        orderId: string;
        locationId: string;
        preorderDateTime: Date;
        ordererName: string;
        isRegularPreorder: boolean;
        reservationDetails?: IReservationDetails;
        roomId: string;
        orderUseCaseId?: string;
        offerId: string;
    };

    const emit = defineEmits(["orderCancelled", "filterSelectionChanged"]);

    const props = defineProps<{
        preorders: IPreorder[];
        filterOptions: Array<ILinkableEntity>;
        defaultSelections: Array<ILinkableEntity>;
        selectedShifts: IPreorderShift[];
        isLoading: boolean;
    }>();

    const stateSelection = ref<Array<ILinkableEntity>>(props.defaultSelections);

    const showDate = ref(false);
    const tableReservationStateSelectionMessage = computed(
        () => `${trans.settings.status} (${stateSelection.value.length})`
    );
    const preordersList = computed(() => {
        const result: PreorderListItem[] = [];

        for (const preorder of props.preorders) {
            if (preorder.hasNonReservationItem) {
                result.push({
                    listItemId: preorder.orderId,
                    orderId: preorder.orderId,
                    locationId: preorder.locationId,
                    preorderDateTime: preorder.preorderDateTime,
                    ordererName: preorder.ordererName,
                    isRegularPreorder: true,
                    roomId: preorder.roomId,
                    orderUseCaseId: preorder.orderUseCaseId,
                    offerId: preorder.offerId,
                });
            } else if (preorder.hasTableReservation) {
                const reservation = preorder.reservation!;

                result.push({
                    listItemId: `${preorder.orderId}-${reservation.orderItemId}`,
                    orderId: preorder.orderId,
                    locationId: preorder.locationId,
                    preorderDateTime: preorder.preorderDateTime,
                    ordererName: preorder.ordererName,
                    reservationDetails: reservation,
                    isRegularPreorder: false,
                    roomId: preorder.roomId,
                    orderUseCaseId: preorder.orderUseCaseId,
                    offerId: preorder.offerId,
                });
            }
        }
        return result;
    });

    const filteredPreorders = computed(() => {
        if (props.selectedShifts.length === 0) {
            return preordersList.value;
        }

        const result: PreorderListItem[] = [];
        for (const preorder of preordersList.value) {
            for (const shift of props.selectedShifts) {
                if (matchesShift(preorder, shift)) {
                    result.push(preorder);
                    break;
                }
            }
        }
        return result.sort((a, b) => a.preorderDateTime.getTime() - b.preorderDateTime.getTime());
    });

    function onUpdateTableReservationStateFilterSelection(value: ILinkableEntity[]) {
        emit("filterSelectionChanged", value);
    }

    function matchesShift(preorder: PreorderListItem, shift: IPreorderShift): boolean {
        const isShiftInTimeRange = isInShift(preorder, shift);
        if (!isShiftInTimeRange) return false;

        const isShiftAssignedToRoom = shift.roomIds.includes(preorder.roomId);
        if (!isShiftAssignedToRoom) return false;

        const isShiftAssignedToOrderUseCase =
            preorder.orderUseCaseId != null && shift.orderUseCaseIds.includes(preorder.orderUseCaseId);
        if (!isShiftAssignedToOrderUseCase) return false;

        const isShiftAssignedToOffer = shift.offerId === preorder.offerId;
        if (!isShiftAssignedToOffer) return false;

        return true;
    }

    function isInShift(preorder: PreorderListItem, shift: IPreorderShift): boolean {
        const startsInShift =
            dateIsInRange(preorder.preorderDateTime, shift.startUtc, shift.endUtc) &&
            shift.endUtc.getTime() !== preorder.preorderDateTime.getTime();
        if (preorder.isRegularPreorder) return startsInShift;

        const endsInShift = dateIsInRange(preorder.reservationDetails!.timeTo, shift.startUtc, shift.endUtc);
        const lastsInShift =
            startedBefore(preorder.reservationDetails!.timeFrom, shift.startUtc) &&
            endsAfter(preorder.reservationDetails!.timeTo, shift.endUtc);

        return startsInShift || endsInShift || lastsInShift;
    }

    function dateIsInRange(date: Date, start: Date, end: Date): boolean {
        return date >= start && date <= end;
    }

    function startedBefore(date: Date, shiftStart: Date): boolean {
        return date < shiftStart;
    }

    function endsAfter(date: Date, shiftEnd: Date): boolean {
        return date > shiftEnd;
    }

    // date/time formatting is handled in PreordersListRow

    function onPreorderClick(orderId: string) {
        new OrderDetailsModal(orderId, () => {
            emit("orderCancelled");
        }).show();
    }

    function onReservationDetailsChanged(newReservationDetails: IReservationDetails) {
        // update original row
        for (const preorder of props.preorders) {
            const reservation = preorder.reservation;
            if (reservation != null) {
                if (reservation.orderItemId === newReservationDetails.orderItemId) {
                    reservation.tableAssignments = newReservationDetails.tableAssignments;
                    break;
                }
            }
        }
    }
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";

    .preorders-list {
        flex: 1;
        display: flex;
        flex-direction: column;
        height: 100%;

        > header {
            padding: 0.5em 1em;
            background-color: #484848;
            color: #fff;
            font-weight: 500;
            flex-shrink: 0;
        }

        .preorders-grid-container {
            flex: 1;
            display: flex;
            flex-direction: column;
            overflow: hidden;
        }

        .grid-header {
            display: grid;
            grid-template-columns: 80px 1fr 50px;
            background-color: #f8f9fa;
            border-bottom: 1px solid #ccc;
            position: sticky;
            top: 0;
            z-index: 10;

            &.with-date {
                grid-template-columns: 120px 1fr 50px;
            }
        }

        .grid-rows-scrollable {
            flex: 1;
            position: relative;
        }

        .grid-row {
            display: grid;
            grid-template-columns: 80px 1fr 50px;
            border-bottom: 1px solid #ccc;
            min-height: 60px;
            cursor: pointer;
            transition: all 0.2s ease-in-out;

            &:nth-child(even) {
                background-color: #f0f0f0;
            }

            &:nth-child(odd) {
                background-color: #fff;
            }

            &:hover {
                background-color: colors.$light-gray-100 !important;
                // transform: translateX(3px);
            }

            &.with-date {
                grid-template-columns: 120px 1fr 50px;
            }

            &.is-editing {
                cursor: default;
            }
        }

        .grid-cell {
            display: flex;
            align-items: center;
            padding: 0.5em;

            &.time-header,
            &.name-header,
            &.guests-header {
                font-weight: 500;
                padding-top: 0.5em;
                padding-bottom: 0.5em;
            }

            &.guests-header {
                justify-content: flex-end;
                padding-right: 1em;

                > .select-preorder-state-component {
                    margin: 0 12px;
                    min-width: 200px;
                }
            }

            &.time-cell {
                flex-direction: column;
                align-items: flex-start;
                padding-left: 1em;
                padding-right: 0.5em;
                white-space: nowrap;
            }

            &.name-cell {
                align-items: flex-start;
                padding-left: 1em;
                padding-top: 0.75em;
                padding-bottom: 0.75em;
                display: block;
                max-width: 420px;
            }

            &.guests-cell {
                justify-content: flex-end;
                padding-left: 0.5em;
                padding-right: 1em;
                font-size: 14px;
                font-weight: 400;
                color: colors.$fontColor;
            }
        }

        .preorder-date,
        .time-from,
        .time-to {
            display: block;
            margin-bottom: -0.25em;

            i {
                margin-right: 0.25em;
                font-size: 0.9em;
            }
        }

        .time-to {
            margin-bottom: 0;
        }

        .guest-name {
            font-size: 16px;
            font-weight: 600;
            color: colors.$fontColor;
            margin-bottom: 0.5em;
        }

        .no-preorders {
            text-align: center;
            padding: 1em;
            color: colors.$fontColor;
        }
    }
</style>
